import Config from "../constnats/config";
import { DescriptorProps } from "../constnats/descriptor";
import { MetadataField } from "../constnats/reportConstants";
import EntityFactory, { CellEntity } from "../pages/admin/company/components/financialTables/table/cellEntity";
import { ReportType } from "../schemas/report.schemas";
import Assert from "./asserts";
import CloneUtils from "./cloneUtils";
import DescriptorType from "./descriptor/descriptorType";
import Utils from "./descriptor/descriptorUtils";
import ObjCheck from "./objCheck";
import ReportUtils from "./reportUtils";
import NumUtils  from './utils'

type ChangeType = {
    value: any;
    correction: any;
    calcsDisabled: any;
    manuallyCalculated: any;
    reportedAsLabel: any;
};

type FieldChangeType = {
    new: ChangeType;
    old: ChangeType;
};

type ReportChangesType = Record<string, FieldChangeType>;


export const UPDATA_STRATEGY = {
    REPLACE_CORE_VALUES: 'REPLACE_CORE_VALUES',
    PRESERVE_NON_EMPTY_CORE_VALUES: 'PRESERVE_NON_EMPTY_CORE_VALUES',
    APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES:
        'APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES',
} as const;

export function getChangeListener(report: ReportType, reportChanges: ReportChangesType) {
    return (descriptorChange: boolean, entity: CellEntity) => {
        const descriptorId = entity.descriptorId();
        const oldDataRaw = report.dynamicFields[descriptorId];
        const oldData = oldDataRaw === undefined ? '' : oldDataRaw;
        const fieldSpecificMetadata = report.metadata.fieldSpecific[descriptorId];
        const oldCorrectionData = fieldSpecificMetadata?.[MetadataField.CORRECTION] || '0';
        const oldCalcsDisabled = !!fieldSpecificMetadata?.[MetadataField.CALCS_DISABLED];
        const oldManuallyCalculated = !!fieldSpecificMetadata?.[MetadataField.MANUALLY_CALCED];
        const oldReportedAsLabel = fieldSpecificMetadata?.reportedAsLabel;

        if (descriptorChange === true) {
            const newMetadata = entity.getMetadata();
            const newData = entity.getData();
            const newCorrectionData = newMetadata[MetadataField.CORRECTION];
            const newCalcsDisabled = entity.getCalcsDisabled();
            const newManuallyCalculated = !!newMetadata[MetadataField.MANUALLY_CALCED];
            const newReportedAsLabel = newMetadata[MetadataField.REPORTED_AS_LABEL];
            if (!(newData === '-' &&
                ObjCheck.isNullUndefinedEmptyOrDash(oldData) &&
                newCorrectionData == oldCorrectionData &&
                normalizeBoolean(newCalcsDisabled) == normalizeBoolean(oldCalcsDisabled) &&
                normalizeBoolean(newManuallyCalculated) == normalizeBoolean(oldManuallyCalculated) &&
                normalizeLabel(newReportedAsLabel) == normalizeLabel(oldReportedAsLabel)
            )) {
                //TODO Find more elegant approach
                reportChanges[descriptorId] = {
                    new: {
                        value: newData,
                        correction: newCorrectionData,
                        calcsDisabled: newCalcsDisabled,
                        manuallyCalculated: newManuallyCalculated,
                        reportedAsLabel: oldReportedAsLabel,
                    },
                    old: {
                        value: oldData,
                        correction: oldCorrectionData,
                        calcsDisabled: oldCalcsDisabled,
                        manuallyCalculated: oldManuallyCalculated,
                        reportedAsLabel: newReportedAsLabel,
                    },
                };    
            }
        }
    };
}

function normalizeBoolean(b:any) {
    return b === true
}

function normalizeLabel(l:any) {
    return ObjCheck.isNullUndefinedOrEmpty(l) ? "" : l
}

export class ReportUpdateManager {
    report: ReportType;
    descriptors: any[];
    reportChanges: ReportChangesType;
    dryRunEntries: CellEntity[];
    descriptorsToUpdateStrategyMap: any

    constructor(report: ReportType, descriptors: any[], descriptorsToUpdateStrategyMap: any) {
        this.report = report;
        this.descriptors = descriptors;
        this.reportChanges = {};
        this.descriptorsToUpdateStrategyMap = descriptorsToUpdateStrategyMap

        const changeListener = getChangeListener(this.report, this.reportChanges);

        this.dryRunEntries = new EntityFactory(changeListener).create(descriptors, CloneUtils.deepClone(this.report));

        for (const entity of this.dryRunEntries) {
            entity.refresh();
        }
    }

    remediate() {
        for (const entity of this.dryRunEntries) {
            const descriptor = entity.getDescriptor();
            const descriptorId = descriptor[DescriptorProps.ID]
            let oldData = entity.getOriginalData().replace("%", "")
            const newData = entity.getData().replace("%", "")
            if (newData !== oldData) {
                /**
                 * descriptorsToUpdateStrategyMap holds dynamic descriptorIds (it missing year period format etc).
                 * Due to this we get an error if we try and access a non-dynamic descriptor id.
                 * Here we check if the descriptorsToUpdateStrategyMap has a value before checking to see if the strategy
                 * is included
                 */
                if (this.descriptorsToUpdateStrategyMap[descriptorId]?.includes(UPDATA_STRATEGY.PRESERVE_NON_EMPTY_CORE_VALUES)) {
                    if (Utils.hasCalculation(descriptor) && NumUtils.isNumber(oldData)) {
                        if (NumUtils.isNumber(newData)) {
                            let correction = ((NumUtils.toNumber(newData) - NumUtils.toNumber(oldData))) * -1;
                            correction = correction + NumUtils.toNumber(entity.getMetadata()[MetadataField.CORRECTION])
                            entity.setMetadata({
                                ...entity.getMetadata(),
                                [MetadataField.CORRECTION]: NumUtils.toReadMode(correction, Config.FLOATING_POINT_PRECISION)
                            })
                        } else {
                            entity.setMetadata({ ...entity.getMetadata(), [MetadataField.CALCS_DISABLED]: true })
                            if (DescriptorType.isPercent(descriptor)) {
                                oldData = oldData + "%"
                            }
                            entity.setData(oldData, undefined)
                        }
                    }
                }

                if (this.descriptorsToUpdateStrategyMap[descriptorId]?.includes(UPDATA_STRATEGY.APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES)) {
                    if (ObjCheck.isNullUndefinedEmptyOrDash(oldData)) {
                        entity.setMetadata({
                            ...entity.getMetadata(),
                            [MetadataField.MANUALLY_CALCED]: true
                        })
                    }
                }
            }
        }
    }

    setCoreValue(descriptorId: string, newValue: any) {
        const entity = this.dryRunEntries.find((e) => e.descriptorId() === descriptorId);
        if (!entity) {
            Assert.fail(
                `reportDiffUtil > setReportedAsLabel > Entity was not found with the following data: reportId:${this.report.id} targetDescriptorId:${descriptorId}`
            );
            return;
        }

        entity.setData(newValue, undefined);
    }

    setReportedAsLabel(descriptorId: string, newValue: any) {
        const entity = this.dryRunEntries.find((e) => e.descriptorId() === descriptorId);

        if (!entity) {
            Assert.fail(
                `reportDiffUtil > setReportedAsLabel > Entity was not found with the following data: reportId:${this.report.id} targetDescriptorId:${descriptorId}`
            );
            return;
        }

        const newMetadata = { ...entity.getMetadata(), [MetadataField.REPORTED_AS_LABEL]: newValue };
        entity.setMetadata(newMetadata);
    }

    getDiffs() {
        return this.reportChanges;
    }

    getUpdatedReport() {
        // TODO IMPORTANT: This is here to account for fields with enabled calculation
        // it will override the field to it's previous value if there is an enabled calculation
        for (const entity of this.dryRunEntries) {
            entity.refresh();
        }
        return ReportUtils.cellEntitiesToReport(this.dryRunEntries, this.report);
    }
}
